import { Params, SortingInfo, ValueSet } from '../../types';
import { GROUP_DELIMITER, LEADERBOARD_ROWID_MAP, MODEL_IDS_DELIMITER } from './constants';
import { getSortKeysFromColumn, GroupInfo, LeaderboardInfo, LeaderColumn } from './data';
import { LeaderboardType, TableCustomiseOption, ProductGroup } from './types';

export const createProductGroupsParam = (response: ValueSet) => {
  if (!response) return [];
  const productGroups = Object.keys(response).reduce((acc, group) => {
    acc.push({ groupName: group, modelIds: response[group].modelIds });
    return acc;
  }, []);

  return productGroups;
};

export const getGroupDataFromLeaderboard = (
  leaderboardData: LeaderboardInfo,
  groups: ProductGroup[],
  rowIdField: string
): GroupInfo[] => {
  if (!leaderboardData || !groups) return undefined;
  const groupData = leaderboardData.rowIds
    .filter(rowId => groups?.find(group => group.id === rowId))
    .map(rowId => {
      const group = groups?.find(group => group.id === rowId);
      const groupSummary = leaderboardData.rowsData.find(row => row.values[`${rowIdField}`] === group.name);
      const groupProducts = leaderboardData.rowsData.filter(row =>
        group?.modelIds.includes(`${row.values[`${rowIdField}`]}`)
      );
      const updatedGroupProducts = groupProducts.map(row => ({
        ...row,
        values: {
          ...row.values,
          group: group.name, // Add group name as a field into the data
        },
      }));
      return { name: group.name, data: updatedGroupProducts, groupSummary, groupModelIds: group.modelIds };
    });
  // sort by modelIds length
  const sorted = groupData.sort((a, b) => b.groupModelIds.length - a.groupModelIds.length);
  return sorted;
};

export const sortingColumnOptions = (col: LeaderColumn, sortIdex: number, sortingInfo: SortingInfo) => {
  const hasSorting = col.column.sortOptions && !col.subLabels?.length;
  const identifier = getSortKeysFromColumn(col);
  const isSorted = hasSorting && identifier.includes(sortingInfo?.sortKey) && sortingInfo?.sortIndex === sortIdex;

  return { hasSorting, identifier, isSorted };
};

export const getColumnIdentifier = (columns: LeaderColumn[], col: LeaderColumn) =>
  columns.findIndex(cl => `${cl.column.name}.${col.column.label}` === `${col.column.name}.${col.column.label}`);

export const findRelatedGroup = (rowId: string, groupData: GroupInfo[]) => {
  return groupData?.find(item => item.name === rowId);
};

export const getCustomiseOptionLabels = (options: TableCustomiseOption[], optionSettings: Params) => {
  if (!options || !optionSettings) {
    return {};
  }
  return Object.keys(optionSettings).reduce((acc, key) => {
    acc[key] = options
      .filter(option => option.paramName === key)
      .flatMap(option => option.options.filter(opt => optionSettings[key].includes(opt.value)).map(opt => opt.label));
    return acc;
  }, {});
};

/**
 * leaderboardGroupsIntoParams
 * converts group object into Params type
 * example: {values:[{groupName: 'hello', modelIds: ['1', '2']}] @returns ['hello|1,2']
 */

export const leaderboardGroupsIntoParams = (values: { groupName: string; modelIds: string[] }[]): string[] => {
  return values.map(group => {
    return leaderboardGroupIntoParams(group);
  });
};
export const leaderboardGroupIntoParams = (value: { groupName: string; modelIds: string[] }): string => {
  const modelIdsStr: string = value.modelIds.join(MODEL_IDS_DELIMITER);
  const groupString = `${value.groupName}${GROUP_DELIMITER}${modelIdsStr}`;
  return groupString;
};

export const findLeaderboardItemNamesForTracking = (
  ids: (string | number)[],
  rowIdField: string,
  rowNameField: string,
  leaderboardData?: LeaderboardInfo
) => {
  if (!leaderboardData) {
    return [];
  }
  return ids.map(
    id => leaderboardData.rowsData.find(data => data.values[rowIdField] == id)?.values[rowNameField] as string
  );
};

export const isSegmentFeature = (field: string): boolean => {
  return field.startsWith('segment');
};

export const isProductFeature = (field: string): boolean => {
  return field.startsWith('f');
};

export const getLeaderboardHeaderString = (type: LeaderboardType, length: number): string => {
  if (length > 1) {
    return getPluralisedLeaderboardType(type);
  }
  return type;
};

export const getPluralisedLeaderboardType = (type: LeaderboardType): string => {
  if (type === LeaderboardType.Country) {
    return 'countries';
  }
  return `${type}s`;
};

export const getLeaderboardType = (rowIdField: string): LeaderboardType => {
  // returns the leaderboard type based on the rowIdField
  // if the rowIdField doesn't match the predefined leaderboard row ids in the map,
  // then check if it's a segment feature or a product feature and return accordingly
  return (
    LEADERBOARD_ROWID_MAP[rowIdField] ||
    (isSegmentFeature(rowIdField) && LeaderboardType.SegmentFeature) ||
    (isProductFeature(rowIdField) && LeaderboardType.ProductFeature)
  );
};
